import { useEffect, useState } from "react"
import { listModulesApi } from "../../modules/api/modules.api"
import { listLessonsApi } from "../../lessons/api/lesson.api"

export type LessonMapEntry = { lessonId: string; title: string; moduleId: string; moduleTitle: string }
export type ModuleGroup = { moduleId: string; title: string; lessons: { id: string; title: string }[] }

/**
 * The backend only stores a message→lesson reference (see mentor_chat_lesson_context.pdf) —
 * there's no server-side module grouping. We rebuild lesson→module locally from the course
 * structure the admin/mentor panel already has access to.
 */
export const useCourseLessonMap = (courseId?: string) => {
  const [modules, setModules] = useState<ModuleGroup[]>([])
  const [lessonMap, setLessonMap] = useState<Record<string, LessonMapEntry>>({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!courseId) {
      setModules([])
      setLessonMap({})
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)

    ;(async () => {
      try {
        const modsRes = await listModulesApi(courseId)
        const mods = [...modsRes.data.items].sort((a, b) => a.order_number - b.order_number)

        const groups: ModuleGroup[] = []
        const map: Record<string, LessonMapEntry> = {}

        await Promise.all(
          mods.map(async (mod) => {
            const lessonsRes = await listLessonsApi(mod.id)
            const lessons = [...lessonsRes.data.items]
              .sort((a, b) => a.order_number - b.order_number)
              .map((l) => ({ id: l.id, title: l.title }))
            groups[mods.indexOf(mod)] = { moduleId: mod.id, title: mod.title, lessons }
            lessons.forEach((l) => {
              map[l.id] = { lessonId: l.id, title: l.title, moduleId: mod.id, moduleTitle: mod.title }
            })
          })
        )

        if (!cancelled) {
          setModules(groups)
          setLessonMap(map)
        }
      } catch {
        // Course structure unavailable — degrade gracefully, the lesson filter just won't show.
        if (!cancelled) {
          setModules([])
          setLessonMap({})
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => { cancelled = true }
  }, [courseId])

  return { modules, lessonMap, loading }
}
